import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { cn } from "@/lib/utils"
import { assignMissionPath } from "@/routes"
import type { MissionAnalyticsFunnelBucket } from "@/types/mission-analytics"

/**
 * OKS-63 — Per-mission collab-state funnel chart.
 *
 * Server emits RAW per-state counts for every `Collab::STATES` key (TRD
 * K10); the FE combines display buckets here per UX Spec §2:
 *
 *   Display bucket           | Backend state(s) summed
 *   -------------------------+-----------------------------------------
 *   Assigned                 | assigned
 *   Applied                  | applied
 *   Accepted                 | accepted
 *   Rejected                 | rejected
 *   Purchased                | purchased
 *   Shipping                 | to_ship + shipped + delivered
 *   Posted                   | posted
 *   Completed                | completed
 *   Declined                 | declined
 *   Issue                    | issue
 *
 * Horizontal `BarChart` (vertical layout in Recharts terms — `layout="vertical"`
 * + YAxis as the category axis). One bar per display bucket, color
 * cycles through `--chart-1..7`.
 *
 * Empty state (all zero): the funnel renders an empty-card variant
 * with the "Assign creators" CTA → `assignMissionPath(id)`.
 */
export interface AnalyticsCollabFunnelChartProps {
  funnel: MissionAnalyticsFunnelBucket[]
  missionId: number
  missionTitle: string
  className?: string
}

interface DisplayRow {
  bucket: string
  count: number
}

export function AnalyticsCollabFunnelChart({
  funnel,
  missionId,
  missionTitle,
  className,
}: AnalyticsCollabFunnelChartProps) {
  const { t } = useTranslation()

  const rows: DisplayRow[] = useMemo(
    () => buildDisplayRows(funnel, t),
    [funnel, t],
  )
  const total = rows.reduce((acc, r) => acc + r.count, 0)
  const isEmpty = total === 0

  const config: ChartConfig = useMemo(
    () => ({
      count: {
        label: t("analytics.funnel.mirror_table.count"),
        theme: { light: "var(--chart-1)", dark: "var(--chart-1)" },
      },
    }),
    [t],
  )

  return (
    <Card
      data-testid="analytics-detail-funnel-chart"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("analytics.funnel.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyState missionId={missionId} />
        ) : (
          <ChartBody config={config} rows={rows} missionTitle={missionTitle} />
        )}
        <SrOnlyTable rows={rows} />
      </CardContent>
    </Card>
  )
}

interface ChartBodyProps {
  config: ChartConfig
  rows: DisplayRow[]
  missionTitle: string
}

function ChartBody({ config, rows, missionTitle }: ChartBodyProps) {
  const { t } = useTranslation()
  const ariaSummary = t("analytics.funnel.aria_summary", {
    title: missionTitle,
    counts: rows.map((r) => `${r.bucket}: ${r.count}`).join(", "),
  })

  return (
    <div
      data-testid="analytics-detail-funnel-chart-canvas"
      role="img"
      aria-label={ariaSummary}
    >
      <ChartContainer config={config} className="aspect-auto h-[320px] w-full">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ left: 12, right: 16, top: 8, bottom: 8 }}
        >
          <CartesianGrid horizontal={false} strokeDasharray="3 3" />
          <XAxis
            type="number"
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tickMargin={6}
          />
          <YAxis
            dataKey="bucket"
            type="category"
            tickLine={false}
            axisLine={false}
            width={96}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Bar
            dataKey="count"
            fill="var(--color-count)"
            radius={[0, 4, 4, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ChartContainer>
    </div>
  )
}

function EmptyState({ missionId }: { missionId: number }) {
  const { t } = useTranslation()
  return (
    <div
      data-testid="analytics-detail-funnel-chart-empty"
      className="text-muted-foreground flex h-[200px] flex-col items-center justify-center gap-3 text-sm"
    >
      <p className="text-foreground text-sm font-medium">
        {t("analytics.funnel.empty.title")}
      </p>
      <a
        href={assignMissionPath(missionId)}
        className="text-primary text-sm font-medium hover:underline"
        data-testid="analytics-detail-funnel-chart-empty-cta"
      >
        {t("analytics.funnel.empty.cta")}
      </a>
    </div>
  )
}

function SrOnlyTable({ rows }: { rows: DisplayRow[] }) {
  const { t } = useTranslation()
  return (
    <table
      className="sr-only"
      data-testid="analytics-detail-funnel-chart-mirror-table"
    >
      <caption>{t("analytics.funnel.mirror_table_caption")}</caption>
      <thead>
        <tr>
          <th>{t("analytics.funnel.mirror_table.state")}</th>
          <th>{t("analytics.funnel.mirror_table.count")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.bucket}>
            <td>{row.bucket}</td>
            <td>{row.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Combine raw per-state counts into the UX-Spec display buckets.
 * Server emits every state in `FUNNEL_STATE_ORDER` (14 since OKS-483,
 * restored in OKS-1005); we read by key, so states outside the buckets
 * below — `expired`, `reopen_requested` — are simply not drawn. We collapse
 * `to_ship + shipped + delivered → "Shipping"` and pass through the
 * rest. Display order matches the funnel flow.
 */
function buildDisplayRows(
  funnel: MissionAnalyticsFunnelBucket[],
  t: (key: string) => string,
): DisplayRow[] {
  const byState = new Map<string, number>(funnel.map((b) => [b.state, b.count]))

  const get = (s: string) => byState.get(s) ?? 0

  return [
    { bucket: t("analytics.funnel.buckets.assigned"), count: get("assigned") },
    { bucket: t("analytics.funnel.buckets.applied"), count: get("applied") },
    { bucket: t("analytics.funnel.buckets.accepted"), count: get("accepted") },
    { bucket: t("analytics.funnel.buckets.rejected"), count: get("rejected") },
    {
      bucket: t("analytics.funnel.buckets.purchased"),
      count: get("purchased"),
    },
    {
      bucket: t("analytics.funnel.buckets.shipping"),
      count: get("to_ship") + get("shipped") + get("delivered"),
    },
    { bucket: t("analytics.funnel.buckets.posted"), count: get("posted") },
    {
      bucket: t("analytics.funnel.buckets.completed"),
      count: get("completed"),
    },
    { bucket: t("analytics.funnel.buckets.declined"), count: get("declined") },
    { bucket: t("analytics.funnel.buckets.issue"), count: get("issue") },
  ]
}
